import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation } from "react-router-dom";
import {
  Briefcase,
  Building2,
  Headset,
  Mail,
  Phone,
  PhoneCall,
  Search,
  User,
} from "lucide-react";
import AgentLayout from "../../../agentComponents/AgentLayout";
import { AGENT_INK, AGENT_LINE, AGENT_MUTED } from "../../../agentComponents/agentTheme";
import AgentAccordionFaq from "./AgentFaq";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import CustomSnackbar from "../../../components/CustomSnackbar";
import axiosInstance from "../../../services/axiosInstance";

// Where an agent lands on a call: look the caller up by DID, then read the
// client's details and FAQs. The lookup is the whole job, so it owns the top of
// the page rather than sitting in a column beside everything else.

const CARD = {
  bgcolor: "#fff",
  borderRadius: "16px",
  border: `1px solid ${AGENT_LINE}`,
  p: 3,
};

const AgentDashboard = () => {
  const location = useLocation();
  const vendorNo = localStorage.getItem("vendorNo");

  const [userDetails, setUserDetails] = useState(null);
  const [faqDetails, setFaqDetails] = useState([]);
  const [searching, setSearching] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [connectedCall, setConnectedCall] = useState({
    leadID: "",
    vendorID: "",
    listID: "",
    phoneNumber: "",
    callDate: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const vendorId = params.get("did_pattern");
    setConnectedCall({
      leadID: params.get("lead_id"),
      vendorID: vendorId,
      listID: params.get("list_id"),
      phoneNumber: params.get("phone_number"),
      callDate: params.get("entry_date"),
    });
    localStorage.setItem("vendorNo", vendorId);
  }, [location]);

  useEffect(() => {
    if (connectedCall?.phoneNumber) formik.setFieldValue("didNumber", vendorNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedCall?.phoneNumber]);

  useEffect(() => {
    if (Object.values(connectedCall).some(Boolean)) webHookData(connectedCall);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedCall]);

  const webHookData = async (data) => {
    try {
      await axiosInstance.post(apiEndPointsConfig.saveWebHookData, data);
    } catch (error) {
      console.error(error);
    }
  };

  const getDetailsByDidNo = async (phoneNumber) => {
    setSearching(true);
    try {
      const res = await axiosInstance.get(`${apiEndPointsConfig.searchByDID}/${phoneNumber}`);
      if (res.data.success) {
        setFaqDetails(res?.data?.faqs || []);
        setUserDetails(res?.data?.user || null);
        localStorage.setItem("didNumber", phoneNumber);
        localStorage.setItem("userId", res?.data?.user?._id);
        localStorage.setItem("businessName", res?.data?.user?.companyName);
        setSnackbar({ open: true, message: "Client found.", severity: "success" });
      } else {
        setFaqDetails([]);
        setUserDetails(null);
        setSnackbar({ open: true, message: "No client found for this DID.", severity: "warning" });
      }
    } catch (error) {
      console.error(error);
      setFaqDetails([]);
      setUserDetails(null);
      setSnackbar({ open: true, message: "No client found for this DID.", severity: "warning" });
    } finally {
      setSearching(false);
    }
  };

  const formik = useFormik({
    initialValues: { didNumber: "" },
    validationSchema: Yup.object({
      didNumber: Yup.string()
        .required("DID number is required")
        .matches(/^\d+$/, "Only numbers are allowed"),
    }),
    onSubmit: ({ didNumber }) => {
      getDetailsByDidNo(didNumber === "null" ? vendorNo : didNumber);
    },
  });

  const fields = userDetails
    ? [
        { label: "Full Name", value: userDetails.name, icon: <User size={16} /> },
        { label: "Email", value: userDetails.email, icon: <Mail size={16} /> },
        { label: "Phone Number", value: userDetails.phoneNumber, icon: <Phone size={16} /> },
        { label: "Company", value: userDetails.companyName, icon: <Building2 size={16} /> },
        { label: "Industry", value: userDetails.industryType, icon: <Briefcase size={16} /> },
        { label: "Care Number", value: userDetails.customerCareNo, icon: <Headset size={16} /> },
      ]
    : [];

  return (
    <AgentLayout subtitle="Look up the caller, then work from their FAQs.">
      <Grid container spacing={2.5}>
        {/* ── Lookup ───────────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Box sx={CARD}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "12px",
                  bgcolor: "#EEF2FF",
                  color: "#0C237B",
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PhoneCall size={21} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: AGENT_INK }}>
                  Caller Lookup
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: AGENT_MUTED }}>
                  Enter the DID the call came in on.
                </Typography>
              </Box>

              <Box
                component="form"
                onSubmit={formik.handleSubmit}
                sx={{ display: "flex", gap: 1.25, width: { xs: "100%", md: "auto" } }}
              >
                <TextField
                  id="didNumber"
                  name="didNumber"
                  size="small"
                  placeholder="DID number"
                  value={formik.values.didNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.didNumber && Boolean(formik.errors.didNumber)}
                  helperText={formik.touched.didNumber && formik.errors.didNumber}
                  sx={{
                    width: { xs: "100%", md: 240 },
                    "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#F8FAFC" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={17} color="#94A3B8" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={searching}
                  sx={{
                    px: 3,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: "#0C237B",
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: "#1A3399" },
                  }}
                >
                  {searching ? "Searching..." : "Search"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* ── Client card ──────────────────────────────────────────────── */}
        {userDetails && (
          <Grid item xs={12}>
            <Box sx={CARD}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  pb: 2.5,
                  mb: 2.5,
                  borderBottom: `1px solid ${AGENT_LINE}`,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    bgcolor: "#0C237B",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  {(userDetails.companyName || userDetails.name || "?").charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: AGENT_INK }} noWrap>
                    {userDetails.companyName || userDetails.name || "Client"}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: AGENT_MUTED }} noWrap>
                    DID {localStorage.getItem("didNumber") || "--"}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${faqDetails.length} FAQ${faqDetails.length === 1 ? "" : "s"}`}
                  sx={{ bgcolor: "#ECFDF5", color: "#16A34A", fontWeight: 700, fontSize: 11.5 }}
                />
              </Box>

              <Grid container spacing={2}>
                {fields.map((f) => (
                  <Grid item xs={12} sm={6} md={4} key={f.label}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.75,
                        borderRadius: "11px",
                        bgcolor: "#F8FAFC",
                        border: `1px solid ${AGENT_LINE}`,
                      }}
                    >
                      <Box sx={{ color: "#94A3B8", display: "flex", flexShrink: 0 }}>{f.icon}</Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11.5, color: AGENT_MUTED }}>{f.label}</Typography>
                        <Typography
                          sx={{ fontSize: 13.5, fontWeight: 600, color: AGENT_INK }}
                          noWrap
                          title={f.value || "-"}
                        >
                          {f.value || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        )}

        {/* ── FAQs ─────────────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Box sx={CARD}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: AGENT_INK, mb: 2 }}>
              Knowledge Base
            </Typography>
            {faqDetails.length ? (
              <AgentAccordionFaq faqDetails={faqDetails} />
            ) : (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography sx={{ fontSize: 13.5, color: AGENT_MUTED }}>
                  Search a DID above to load that client's FAQs.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <CustomSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
        vertical="bottom"
        horizontal="right"
      />
    </AgentLayout>
  );
};

export default AgentDashboard;
